<script setup lang="ts">
import { computed } from 'vue';
import InputSwitch from 'primevue/inputswitch';

const props = defineProps<{
  assignment: Record<string, any>;
  isOpen: boolean;
  actionName?: string;
}>();

const emit = defineEmits<{
  'update:isOpen': [value: boolean];
}>();

const isOpenState = computed({
  get: () => props.isOpen,
  set: (value) => emit('update:isOpen', value),
});

const operationName = computed(() => {
  if (props.actionName) return props.actionName;
  const action = props.assignment?.action;
  if (typeof action === "object" && action?.name) return action.name;
  return "Unknown Assignment";
});

/** Read description/price from the action object when available */
const actionDetails = computed(() => {
  const action = props.assignment?.action;
  if (typeof action === "object" && action) return action;
  return null;
});

function handleStatusChange(value: boolean) {
  emit('update:isOpen', value);
}
</script>

<template>
  <div class="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded p-3">
    <div class="flex flex-col gap-2">
      <!-- Operation Name & Status Row -->
      <div class="flex justify-between items-center">
        <h3 class="font-medium text-lg text-gray-800 dark:text-gray-100 m-0">
          {{ operationName }}
        </h3>

        <!-- Status toggle control -->
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium" :class="isOpenState ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
            {{ isOpenState ? 'Open' : 'Closed' }}
          </span>
          <InputSwitch v-model="isOpenState" @update:modelValue="handleStatusChange" />
        </div>
      </div>

      <!-- Additional Details (if available) -->
      <div v-if="actionDetails" class="mt-1 text-sm text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-2">
        <div v-if="actionDetails.description" class="col-span-2">
          <span class="font-medium">Description:</span>
          <span class="ml-1">{{ actionDetails.description }}</span>
        </div>
        <div v-if="actionDetails.price" class="col-span-1">
          <span class="font-medium">Price:</span>
          <span class="ml-1 font-mono">€{{ actionDetails.price }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
